import { EventEmitter, Injectable, inject } from "@angular/core";
import { Location } from "@angular/common";
import { Router } from "@angular/router";
import { Observable, Subject, Subscriber, of, throwError } from "rxjs";
import { catchError, finalize, map, mergeMap, switchMap, tap } from "rxjs/operators";

import { AbstractAuthService, LocalStoreManager } from "@fd/core";
import { DbKeys } from "src/app/modules/shared/enums/db-keys.enum";
import { UserRoles } from "src/app/modules/shared/enums/user-roles.enum";
import { AppRoutes, RouteInfoData } from "src/app/routes";
import { UserDtoInterface, UserDtoModel } from "../../account/models/user-dto.model";
import { AccountApiService } from "../../account/services/account-api.service";

import { environment as appEnvironment } from "src/environments/environment";

@Injectable({ providedIn: "root" })
export class AuthService extends AbstractAuthService<UserDtoInterface> {
  private readonly router = inject(Router);
  private readonly location = inject(Location);
  private readonly localStorage = inject(LocalStoreManager);
  private readonly accountApiService = inject(AccountApiService);

  private readonly refreshing: Subscriber<UserDtoInterface>[] = [];

  private previousIsLoggedInCheck = false;
  private loginStatus = new Subject<boolean>();

  logoutRedirectUrl: string;

  onLogin = new EventEmitter<UserDtoInterface>();

  get environment() {
    // not injecting this so that we can setup DI in the core module
    return appEnvironment;
  }

  get currentUser(): UserDtoInterface {
    const user = this.localStorage.getDataObject<UserDtoInterface>(DbKeys.CurrentUser);
    this.reevaluateLoginStatus(user);

    return user ? Object.assign(new UserDtoModel(), user) : null;
  }

  get isLoggedIn(): boolean {
    return this.currentUser != null;
  }

  get rememberMe(): boolean {
    return this.localStorage.getDataObject<boolean>(DbKeys.RememberMe) === true;
  }

  set rememberMe(value: boolean) {
    this.localStorage.savePermanentData(value, DbKeys.RememberMe);
  }

  get loginUrl() {
    return AppRoutes.login.routerLink;
  }

  get confirmEmailUrl() {
    return AppRoutes.account.confirmEmail.routerLink;
  }

  get notAuthorizedUrl() {
    return AppRoutes.notAuthorized.routerLink;
  }

  get loginRedirectUrl(): string {
    return this.localStorage.getData(DbKeys.ReturnUrl);
  }

  /** Remembers where to send the user after sign-in; login, account and root pages are ignored so retries keep the original page. */
  set loginRedirectUrl(url: string) {
    if (this.isReturnableUrl(url)) {
      this.localStorage.saveSessionData(url, DbKeys.ReturnUrl);
    }
  }

  constructor() {
    super();
    this.initializeLoginStatus();
  }

  redirectLoginUser() {
    const returnUrl = this.loginRedirectUrl || "/";
    this.localStorage.deleteData(DbKeys.ReturnUrl);
    this.router.navigateByUrl(returnUrl);
  }

  redirectLogoutUser() {
    const redirect = this.logoutRedirectUrl ? this.logoutRedirectUrl : this.loginUrl;
    this.logoutRedirectUrl = null;

    this.router.navigate([redirect]);
  }

  redirectForLogin() {
    this.rememberCurrentUrl();
    return this.router.navigateByUrl(this.loginUrl);
  }

  signIn(userName: string, password: string, rememberMe?: boolean, backdoor?: boolean): Observable<UserDtoInterface> {
    let signIn = this.accountApiService
      .signIn(userName, password, rememberMe, backdoor)
      .pipe(map((response: UserDtoInterface) => this.processLoginResponse(response, rememberMe)));

    if (this.isLoggedIn) {
      signIn = this.signOut().pipe(switchMap(() => signIn));
    }

    return signIn;
  }

  externalSignIn() {
    return this.refreshUser().pipe(
      tap(user => {
        this.onLogin.emit(user);
      })
    );
  }

  confirmEmail(emailAddress: string, code: string): Observable<UserDtoInterface> {
    return this.accountApiService
      .confirmEmail(emailAddress, code)
      .pipe(map((response: UserDtoInterface) => this.processLoginResponse(response)));
  }

  refreshUser(): Observable<UserDtoInterface> {
    const service = this;

    return new Observable(subscriber => {
      service.refreshing.push(subscriber);

      if (service.refreshing.length <= 1) {
        const subscription = service.accountApiService
          .me()
          .pipe(
            map((response: UserDtoInterface) => {
              if (response) {
                service.processLoginResponse(response, null, null, false);
              }
              return response;
            }),
            tap(value => {
              while (service.refreshing.length) {
                const sub = service.refreshing.pop();
                sub.next(value);
                sub.complete();
              }
            }),
            catchError(err => {
              while (service.refreshing.length) {
                const sub = service.refreshing.pop();
                sub.error(err);
              }

              if (err && typeof err === "object" && "error" in err) {
                this.rememberCurrentUrl();
                return this.signOut().pipe(switchMap(() => throwError(err.error)));
              }
              return of(null);
            }),
            finalize(() => {
              while (service.refreshing.length) {
                service.refreshing.pop().complete();
              }
            })
          )
          .subscribe();

        return () => subscription.unsubscribe();
      }

      return () => {};
    });
  }

  impersonateRole(reset: boolean, role?: UserRoles) {
    return this.processImpersonationRequest(this.accountApiService.impersonateRole(reset, role));
  }

  impersonateUser(id: string) {
    return this.processImpersonationRequest(this.accountApiService.impersonate(id), true);
  }

  abortImpersonate() {
    return this.processImpersonationRequest(this.accountApiService.abortImpersonate(), true);
  }

  signOut(): Observable<any> {
    while (this.refreshing.length) {
      const sub = this.refreshing.pop();
      sub.complete();
    }

    this.clearUser();
    return this.accountApiService.signOut();
  }

  getLoginStatusEvent(): Observable<boolean> {
    return this.loginStatus.asObservable();
  }

  private processImpersonationRequest(request$: Observable<UserDtoInterface>, reload = false) {
    return request$.pipe(
      mergeMap(user => {
        let route = this.router.routerState.root;

        while (route.firstChild) {
          route = route.firstChild;
        }

        return route.data.pipe(
          map(data => ({
            data,
            user,
          }))
        );
      }),
      map(response => {
        if (response.user) {
          this.processLoginResponse(response.user, undefined, response.data, true, !reload);
          this.loginStatus.next(true);

          if (reload) {
            if (response.data?.permissionView && !this.currentUser.isInRole(...response.data.permissionViews)) {
              window.location.assign(AppRoutes.notAuthorized.routerLink);
            } else {
              window.location.reload();
            }
          }
        }
        return response;
      })
    );
  }

  private processLoginResponse(
    user: UserDtoInterface,
    rememberMe?: boolean,
    data?: RouteInfoData,
    isLogin = true,
    navigateIfUnauthorized = true
  ) {
    rememberMe ??= this.rememberMe;

    if (isLogin) {
      this.onLogin.emit(user);
    }

    this.saveUserDetails(user, rememberMe);
    this.reevaluateLoginStatus(user);

    if (navigateIfUnauthorized && data?.permissionView && !this.currentUser.isInRole(...data.permissionViews)) {
      this.router.navigateByUrl(AppRoutes.notAuthorized.routerLink);
    }

    return user;
  }

  private saveUserDetails(user: UserDtoInterface, rememberMe: boolean) {
    this.localStorage.savePermanentData(rememberMe, DbKeys.RememberMe);
    this.localStorage.saveData(user, DbKeys.CurrentUser);
  }

  private clearUser() {
    this.localStorage.deleteData(DbKeys.UserPermissions);
    this.localStorage.deleteData(DbKeys.CurrentUser);
    this.reevaluateLoginStatus();
  }

  private reevaluateLoginStatus(currentUser?: UserDtoInterface) {
    const user = currentUser || this.localStorage.getDataObject<UserDtoInterface>(DbKeys.CurrentUser);
    const isLoggedIn = user != null;

    if (this.previousIsLoggedInCheck !== isLoggedIn) {
      setTimeout(() => {
        this.loginStatus.next(isLoggedIn);
      });
      if (!isLoggedIn) {
        this.router.navigateByUrl(this.loginUrl);
      }
    }

    this.previousIsLoggedInCheck = isLoggedIn;
  }

  private initializeLoginStatus() {
    this.localStorage.getInitEvent().subscribe(() => {
      this.reevaluateLoginStatus();
    });
  }

  private rememberCurrentUrl() {
    const navigation = this.router.currentNavigation();
    const navigationUrl = navigation ? this.router.serializeUrl(navigation.finalUrl ?? navigation.extractedUrl) : null;

    // On first load router.url is still "/" while the address bar holds the requested page.
    this.loginRedirectUrl = [navigationUrl, this.router.url, this.location.path(true)].find(url =>
      this.isReturnableUrl(url)
    );
  }

  private isReturnableUrl(url: string) {
    if (!url?.startsWith("/") || url.startsWith("//") || url.startsWith("/\\")) {
      return false;
    }

    const path = url.split(/[?#]/)[0].toLowerCase();
    const accountPath = AppRoutes.accountRoot.routerLink.toLowerCase();
    const excludedPaths = ["/", this.loginUrl.toLowerCase(), this.notAuthorizedUrl.toLowerCase()];

    return !excludedPaths.includes(path) && path !== accountPath && !path.startsWith(`${accountPath}/`);
  }
}
